import { booleanLookup } from '../../../common/constants/common';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IApiResponseBody, ISearchResponse } from '../../../common/models/common';
import { IAzureSavingsPlans } from '../../../services/azure/azureSavingsPlans/azureSavingsPlans.model';
import { RootState } from '../../app.model';
import {
  searchAzureSavingsPlans,
  getAzureSavingsPlansById,
  saveAzureSavingsPlans,
  deleteAzureSavingsPlans,
} from './azureSavingsPlans.action';
import { IAzureSavingsPlansState } from './azureSavingsPlans.model';

export const initialState: IAzureSavingsPlansState = {
  search: {
    loading: false,
    hasErrors: false,
    data: [],
    count: 0,
    lookups: {},
    tableName: '',
  },
  tableColumnSelection: {
    id: null,
    table_name: null,
    columns: {},
  },
  getById: {
    loading: false,
    hasErrors: false,
    data: null,
  },
  save: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
  delete: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
};

export const azureSavingsPlansSlice = createSlice({
  name: 'azureSavingsPlans',
  initialState,
  reducers: {
    clearAzureSavingsPlans: () => {
      return initialState;
    },
    clearAzureSavingsPlansMessages: (state) => {
      state.save.messages = [];
      state.delete.messages = [];
    },
    clearAzureSavingsPlansGetById: (state) => {
      state.getById.data = null;
    },
    setTableColumnSelection: (state, action: PayloadAction<{ [key: string]: boolean }>) => {
      state.tableColumnSelection.columns = action.payload;
    },
  },
  extraReducers: {
    // Search
    [searchAzureSavingsPlans.pending.type]: (state) => {
      state.search.loading = true;
    },
    [searchAzureSavingsPlans.fulfilled.type]: (
      state,
      action: PayloadAction<ISearchResponse<IAzureSavingsPlans>>
    ) => {
      const { search_result, ...rest } = action.payload;
      state.search.data = search_result.records;
      state.search.count = search_result.total_count;
      if (JSON.stringify(rest) !== '{}') {
        state.search.lookups = { ...rest, booleanLookup };
      }
      state.search.loading = false;
      state.search.hasErrors = false;
      state.search.tableName = search_result.table_name;
      if (search_result.column_selection) {
        state.tableColumnSelection.id = search_result.column_selection.id;
        const tableSelectionObj = JSON.parse(search_result.column_selection.columns as any);
        if (tableSelectionObj.columns) {
          state.tableColumnSelection.column_orders = tableSelectionObj.column_orders;
          state.tableColumnSelection.columns = tableSelectionObj.columns;
        } else {
          state.tableColumnSelection.columns = tableSelectionObj;
        }
      }
      state.tableColumnSelection.table_name = search_result.table_name;
    },
    [searchAzureSavingsPlans.rejected.type]: (state) => {
      state.search.loading = false;
      state.search.hasErrors = true;
    },

    // Get by id
    [getAzureSavingsPlansById.pending.type]: (state) => {
      state.getById.loading = true;
    },
    [getAzureSavingsPlansById.fulfilled.type]: (
      state,
      action: PayloadAction<IAzureSavingsPlans>
    ) => {
      state.getById.data = action.payload;
      state.getById.loading = false;
      state.getById.hasErrors = false;
    },
    [getAzureSavingsPlansById.rejected.type]: (state) => {
      state.getById.loading = false;
      state.getById.hasErrors = true;
    },

    // Save
    [saveAzureSavingsPlans.pending.type]: (state) => {
      state.save.loading = true;
      state.save.messages = [];
    },
    [saveAzureSavingsPlans.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.save.loading = false;
      state.save.hasErrors = false;
      state.save.messages = action.payload.messages;
    },
    [saveAzureSavingsPlans.rejected.type]: (state) => {
      state.save.loading = false;
      state.save.hasErrors = true;
    },

    // Delete
    [deleteAzureSavingsPlans.pending.type]: (state) => {
      state.delete.loading = true;
      state.delete.messages = [];
    },
    [deleteAzureSavingsPlans.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.delete.loading = false;
      state.delete.hasErrors = false;
      state.delete.messages = action.payload.messages;
    },
    [deleteAzureSavingsPlans.rejected.type]: (state) => {
      state.delete.loading = false;
      state.delete.hasErrors = true;
    },
  },
});

// A selector
export const azureSavingsPlansSelector = (state: RootState) => state.azureSavingsPlans;

// Actions
export const {
  clearAzureSavingsPlans,
  clearAzureSavingsPlansMessages,
  clearAzureSavingsPlansGetById,
  setTableColumnSelection,
} = azureSavingsPlansSlice.actions;

// The reducer
export default azureSavingsPlansSlice.reducer;
